import { Notification, NotificationUpdateRequest } from "../../Contracts/Notification";
import { MultiGetResponse } from "../../Contracts/Common";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import { persist } from "zustand/middleware";
const getNotificationsUrl = "/v1/payments/notifications";
const clearNotificationUrl = "/v1/payments/notifications/clear";
const markReadNotificationUrl = "/v1/payments/notifications/read";

interface NotificationState {
  isLoading: boolean;
  shouldShake: boolean;
  shakingSinceFetchCycles: number;
  notifications: Notification[];
  notificationsCountMap?: Record<string, number>;
  getNotifications: (
    appendResults: boolean,
    getAccessTokenSilently: any,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string
  ) => Promise<void>;
  notificationsListOpened: boolean;
  setNotificationsListOpened: (open: boolean) => void;
  clearNotification: (
    getAccessTokenSilently: any,
    notificationUpdateRequest: NotificationUpdateRequest
  ) => Promise<void>;
  markReadNotification: (
    getAccessTokenSilently: any,
    notificationUpdateRequest: NotificationUpdateRequest
  ) => Promise<void>;
}

const maxShakingCycles = 10;

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => {
      const { getRequest, putRequest } = useRequestStore.getState();

      const getNotifications = async (
        appendResults: boolean,
        getAccessTokenSilently: any,
        limit?: number,
        sort?: string,
        page?: number,
        filters?: string
      ) => {
        if (!appendResults) {
          set({ isLoading: true });
        }
        try {
          const response = await getRequest<MultiGetResponse<Notification>>(
            getAccessTokenSilently,
            getNotificationsUrl,
            {
              limit,
              sort,
              page,
              include_total_count: true,
              filters
            }
          );
          const existingCountMap = get().notificationsCountMap;
          if (
            response.filtered_count_map?.unread &&
            existingCountMap?.unread &&
            response.filtered_count_map.unread > existingCountMap?.unread
          ) {
            set({ shouldShake: true, shakingSinceFetchCycles: 0 });
          }
          if (response.filtered_count_map?.unread === 0) {
            set({ shouldShake: false, shakingSinceFetchCycles: 0 });
          }
          if (appendResults) {
            set({
              notifications: [...get().notifications, ...response.data].reduce(
                (acc: Notification[], current) => {
                  const x = acc.find(item => item.id === current.id);
                  if (!x) {
                    return acc.concat([current]);
                  } else {
                    return acc;
                  }
                },
                []
              ),
              notificationsCountMap: response.filtered_count_map
            });
          } else {
            set({
              notifications: response.data,
              notificationsCountMap: response.filtered_count_map
            });
          }
        } catch (error) {
          console.error("Error fetching notifications", error);
        } finally {
          set({ isLoading: false });
        }
        if (get().shouldShake) {
          set({
            shakingSinceFetchCycles: get().shakingSinceFetchCycles + 1
          });
          if (get().shakingSinceFetchCycles >= maxShakingCycles) {
            set({ shouldShake: false, shakingSinceFetchCycles: 0 });
          }
        }
      };

      const setNotificationsListOpened = (open: boolean) => {
        set({
          shouldShake: false,
          shakingSinceFetchCycles: 0,
          notificationsListOpened: open
        });
      };

      const clearNotification = async (
        getAccessTokenSilently: any,
        notificationUpdateRequest: NotificationUpdateRequest
      ) => {
        set({ isLoading: true });
        try {
          await putRequest(
            getAccessTokenSilently,
            clearNotificationUrl,
            notificationUpdateRequest,
            { include_total_count: true }
          );
          let updatedNotifications;
          let updatedCountMap = get().notificationsCountMap;
          if (notificationUpdateRequest.update_type === "all") {
            updatedNotifications = new Array<Notification>();
            updatedCountMap = {
              ...updatedCountMap,
              unread: 0
            };
          } else {
            updatedNotifications = get().notifications.filter(notification => {
              return !notificationUpdateRequest.ids?.includes(notification.id);
            });
            const clearedReadNotifications =
              get().notifications.filter(notification => {
                return notificationUpdateRequest.ids?.includes(notification.id);
              }).length || 0;
            updatedCountMap = {
              ...updatedCountMap,
              unread: (get()?.notificationsCountMap?.unread || 0) - clearedReadNotifications
            };
          }
          set({
            notifications: updatedNotifications,
            notificationsCountMap: updatedCountMap
          });
        } catch (error) {
          console.error("Error clearing notification", error);
        } finally {
          set({ isLoading: false });
        }
      };

      const markReadNotification = async (
        getAccessTokenSilently: any,
        notificationUpdateRequest: NotificationUpdateRequest
      ) => {
        set({ isLoading: true });
        try {
          await putRequest(
            getAccessTokenSilently,
            markReadNotificationUrl,
            notificationUpdateRequest
          );
          let updatedNotifications;
          let updatedCountMap = get().notificationsCountMap;
          if (notificationUpdateRequest.update_type === "all") {
            updatedNotifications = get().notifications.map(notification => {
              return {
                ...notification,
                read_at: new Date()
              };
            });
            updatedCountMap = {
              ...updatedCountMap,
              unread: 0
            };
          } else {
            updatedNotifications = get().notifications.map(notification => {
              if (notificationUpdateRequest.ids?.includes(notification.id)) {
                return {
                  ...notification,
                  read_at: new Date()
                };
              }
              return notification;
            });
            const clearedReadNotifications =
              get().notifications.filter(notification => {
                return (
                  notification.read_at && notificationUpdateRequest.ids?.includes(notification.id)
                );
              }).length || 0;
            updatedCountMap = {
              ...updatedCountMap,
              unread: (get()?.notificationsCountMap?.unread || 0) - clearedReadNotifications
            };
          }
          set({
            notifications: updatedNotifications,
            notificationsCountMap: updatedCountMap
          });
        } catch (error) {
          console.error("Error marking notification as read", error);
        } finally {
          set({ isLoading: false });
        }
      };

      return {
        shouldShake: false,
        shakingSinceFetchCycles: 0,
        isLoading: false,
        notifications: [],
        notificationsCount: 0,
        notificationsListOpened: false,
        getNotifications,
        setNotificationsListOpened,
        clearNotification,
        markReadNotification
      };
    },
    {
      name: "notifications-state",
      partialize: state => ({
        shouldShake: state.shouldShake,
        shakingSinceFetchCycles: state.shakingSinceFetchCycles
      })
    }
  )
);
